import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  MousePointerClick,
  PencilLine,
  SearchCheck,
  CreditCard,
  Truck,
  Layers,
  Sparkles,
} from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { DirektoriBizBanner } from "@/components/DirektoriBizBanner";
import { DesignGallery } from "@/components/design/DesignGallery";
import { BenefitStrip } from "@/components/BenefitStrip";
import { CardSurface } from "@/components/card/CardSurface";
import { useCatalog } from "@/hooks/useCatalog";
import { useOrder } from "@/state/OrderContext";
import { formatRM } from "@/lib/formatters";

const HERO_SAMPLE = {
  business: {
    namaPenuh: "Aisyah Rahman",
    jawatan: "Pengurus Perniagaan",
    namaPerniagaan: "Aisyah Enterprise",
    bidangBisnes: "Pelanggan Adalah Keutamaan Kami",
    telefon: "012-345 6789",
    email: "hai@aisyah.my",
  },
  businessAddress: {
    line1: "No. 25, Jalan Melati 3",
    postcode: "06700",
    city: "Pendang",
    state: "Kedah",
    country: "Malaysia",
  },
};

const STEPS = [
  { icon: MousePointerClick, title: "PILIH", text: "Pilih jenis kad dan design kegemaran anda." },
  { icon: PencilLine, title: "ISI", text: "Masukkan maklumat yang ingin dicetak pada kad." },
  { icon: SearchCheck, title: "SEMAK", text: "Lihat preview dan pastikan maklumat adalah betul." },
  { icon: CreditCard, title: "BAYAR", text: "Buat pembayaran dan tempahan anda akan diproses." },
];

const ProductPanel = ({ product, startingPrice, gold }) => (
  <article
    className={`rounded-3xl border p-6 sm:p-8 flex flex-col ${
      gold
        ? "border-[#c79a3b]/40 bg-gradient-to-b from-[#fdf8ec] to-white"
        : "border-[#0f3f2f]/12 bg-gradient-to-b from-[#f3f8f5] to-white"
    }`}
    data-testid={`product-card-${product.code.toLowerCase()}`}
  >
    <span
      className={`inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${
        gold ? "bg-[#0b2a20] text-[#e3b84b]" : "bg-[#0f3f2f] text-white"
      }`}
    >
      {gold ? <Sparkles strokeWidth={1.75} className="h-3.5 w-3.5" /> : <Layers strokeWidth={1.75} className="h-3.5 w-3.5" />}
      {product.code}
    </span>

    <h3 className="mt-4 t-h2">{product.name}</h3>
    <p className="mt-2 t-body">{product.description}</p>

    <ul className="mt-5 space-y-2.5 text-sm text-[#2c3a34]">
      {product.highlights.map((h) => (
        <li key={h} className="flex gap-2.5">
          <BadgeCheck
            strokeWidth={1.75}
            className={`h-5 w-5 shrink-0 ${gold ? "text-[#c79a3b]" : "text-[#12876a]"}`}
          />
          <span>{h}</span>
        </li>
      ))}
    </ul>

    <div className="mt-6 pt-5 border-t border-[#0f3f2f]/10">
      <p className="t-label">Dari</p>
      <p className="text-3xl font-extrabold text-[#0f3f2f] font-heading">
        {formatRM(startingPrice)}
      </p>
      <p className="mt-1 t-help">
        Design grafik yang sama tersedia untuk Standard dan Premium.
      </p>
    </div>
  </article>
);

const CompareRow = ({ label, standard, premium }) => (
  <>
    <div className="pt-4 sm:py-3.5 t-label sm:border-b sm:border-[#0f3f2f]/10">{label}</div>
    <div className="pt-1 sm:py-3.5 text-sm text-[#14201b] sm:border-b sm:border-[#0f3f2f]/10">
      <span className="sm:hidden t-help">Standard: </span>
      {standard}
    </div>
    <div className="pb-4 pt-1 sm:py-3.5 text-sm text-[#14201b] sm:border-b sm:border-[#0f3f2f]/10">
      <span className="sm:hidden t-help">Premium: </span>
      {premium}
    </div>
  </>
);

export default function HomePage() {
  const { products, templates, priceOf, loading } = useCatalog();
  const { order, setTemplate } = useOrder();
  const navigate = useNavigate();
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash || loading) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, loading]);

  const standard = products.find((p) => p.code === "STANDARD");
  const premium = products.find((p) => p.code === "PREMIUM");

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const chooseDesign = (templateId) => {
    setTemplate(templateId);
    navigate("/tempah");
  };

  return (
    <div className="min-h-screen">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#f1f7f3] via-[#fbfaf6] to-[#fbfaf6]" />
        <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-[#e3b84b]/12" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-16 sm:pt-20 sm:pb-24">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
            <div>
              <span
                className="inline-flex items-center gap-2 rounded-full border border-[#c79a3b]/35 bg-white/80 px-3 py-1.5 text-xs font-semibold text-[#8a6a1f]"
                data-testid="hero-benefit-badge"
              >
                <BadgeCheck strokeWidth={1.75} className="h-4 w-4" />
                Penyenaraian Direktori Perniagaan Percuma Dengan Setiap Tempahan
              </span>

              <h1 className="mt-6 t-hero">Kad Perniagaan Profesional, Tempah Dengan Mudah</h1>
              <p className="mt-5 t-body max-w-xl sm:text-lg">
                Pilih design, masukkan maklumat anda dan kami uruskan selebihnya.
              </p>

              <div className="mt-7 max-w-xl">
                <BenefitStrip variant="hero" testId="hero-benefit-strip" />
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => scrollTo("galeri-design")}
                  data-testid="hero-cta-primary"
                  className="btn-primary"
                >
                  Pilih Design Sekarang
                  <ArrowRight strokeWidth={1.75} className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollTo("pilihan-kad")}
                  data-testid="hero-cta-secondary"
                  className="btn-secondary"
                >
                  Lihat Pilihan Kad
                </button>
              </div>

              <p className="mt-7 inline-flex items-center gap-2 text-sm text-[#4a5a53]">
                <Truck strokeWidth={1.75} className="h-4 w-4 text-[#12876a]" />
                Harga termasuk penghantaran ke Semenanjung Malaysia.
              </p>
            </div>

            <div className="relative mx-auto w-full max-w-md pb-10">
              <div className="rotate-[-5deg]">
                <CardSurface
                  styleKey="corporate-split"
                  side="front"
                  data={HERO_SAMPLE}
                  testId="hero-card-1"
                  className="shadow-[0_20px_50px_-25px_rgba(11,42,32,0.55)]"
                />
              </div>
              <div className="rotate-[4deg] -mt-14 ml-auto sm:ml-12">
                <CardSurface
                  styleKey="luxury-gold"
                  side="front"
                  data={HERO_SAMPLE}
                  testId="hero-card-2"
                  className="shadow-[0_24px_55px_-25px_rgba(11,42,32,0.6)]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DESIGN GALLERY */}
      <section
        id="galeri-design"
        className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 scroll-mt-20"
        aria-labelledby="galeri-heading"
      >
        <p className="t-label">Katalog Design</p>
        <h2 id="galeri-heading" className="mt-2 t-h1">
          Pilih Design Kad Perniagaan Anda
        </h2>
        <p className="mt-3 t-body max-w-2xl">
          12 design sedia untuk digunakan. Semua design boleh digunakan dengan atau tanpa logo,
          dan tersedia untuk kedua-dua Standard dan Premium.
        </p>

        <div className="mt-9">
          {loading ? (
            <p className="t-body">Memuatkan design…</p>
          ) : (
            <DesignGallery
              templates={templates}
              selectedId={order.templateId}
              onSelect={chooseDesign}
            />
          )}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative py-14 sm:py-20" aria-labelledby="langkah-heading">
        <div className="absolute inset-0 bg-gradient-to-b from-[#f1f7f3] to-[#fbfaf6]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <h2 id="langkah-heading" className="t-h1">
            Empat Langkah Mudah
          </h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.title} data-testid={`step-${s.title.toLowerCase()}`}>
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-white border border-[#0f3f2f]/10 text-[#0f3f2f]">
                    <s.icon strokeWidth={1.75} className="h-5 w-5" />
                  </span>
                  <span className="font-mono text-xs text-[#c79a3b]">0{i + 1}</span>
                </div>
                <h3 className="mt-4 t-h3 tracking-[0.02em]">{s.title}</h3>
                <p className="mt-1.5 t-body text-sm">{s.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 max-w-3xl">
            <BenefitStrip variant="steps" testId="steps-benefit-strip" />
          </div>

        </div>
      </section>

      {/* PRODUCTS */}
      <section
        id="pilihan-kad"
        className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 scroll-mt-20"
        aria-labelledby="pilihan-heading"
      >
        <p className="t-label">Produk Cetakan</p>
        <h2 id="pilihan-heading" className="mt-2 t-h1">
          Pilihan Kad Perniagaan
        </h2>
        <p className="mt-3 t-body max-w-2xl">
          Saiz 86mm × 52mm · Landscape · Cetakan warna penuh depan &amp; belakang. Jenis kad
          dipilih semasa mengisi tempahan.
        </p>

        {loading ? (
          <p className="mt-8 t-body">Memuatkan pilihan kad…</p>
        ) : (
          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            {standard && (
              <ProductPanel product={standard} startingPrice={priceOf("STANDARD", 100)} />
            )}
            {premium && (
              <ProductPanel product={premium} startingPrice={priceOf("PREMIUM", 100)} gold />
            )}
          </div>
        )}
      </section>

      {/* COMPARISON */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-6" aria-labelledby="banding-heading">
        <h2 id="banding-heading" className="t-h1">
          Standard atau Premium?
        </h2>
        <div className="mt-6 panel-soft p-5 sm:p-8" data-testid="product-comparison">
          <div className="hidden sm:grid grid-cols-3 gap-4 pb-3 border-b-2 border-[#0f3f2f]">
            <div className="t-label text-[#6b7a73]">Ciri</div>
            <div className="font-heading font-bold text-[#0f3f2f]">STANDARD</div>
            <div className="font-heading font-bold text-[#0f3f2f]">
              PREMIUM <span className="text-[#c79a3b]">◆</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 divide-y divide-[#0f3f2f]/10 sm:divide-y-0">
            <CompareRow label="Material" standard="Gloss Art Card 310gsm" premium="Gloss Art Card 360gsm" />
            <CompareRow label="Finishing" standard="Gloss Water Based Varnish" premium="Matte Lamination" />
            <CompareRow label="Penampilan" standard="Profesional" premium="Lebih eksklusif" />
            <CompareRow label="Disyorkan" standard="Kegunaan harian" premium="Profesional & pemilik perniagaan" />
            <CompareRow label="Mula dari" standard="RM29" premium="RM35" />
          </div>
          <p className="mt-6 t-body text-sm">
            Standard sesuai untuk penggunaan harian pada harga yang lebih berbaloi. Premium
            menggunakan kad yang lebih tebal dengan kemasan matte untuk penampilan yang lebih
            eksklusif.
          </p>
        </div>
      </section>

      <DirektoriBizBanner />
      <Footer />
    </div>
  );
}
